import usePayment from "hooks/usePayment";
import { sum } from "utils/operator";
import React, { useCallback, useEffect, useMemo } from "react";
import { EnumFormaPagamento } from "services";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { handleSchemaValidation } from "../schemas";
import { useEventBus } from "../hooks/useEventBus";
import { cleanMoneyMask, formatMoney } from "utils";
import { paymentMethodsDict } from "./FormPayment/constants";

const keyPaymentMethods = {
  [EnumFormaPagamento.CREDITO]: "credit",
  [EnumFormaPagamento.DEBITO]: "debit",
  [EnumFormaPagamento.PIX]: "pix",
  [EnumFormaPagamento.MULTIPLE_CREDIT]: "credit",
};

interface FormPaymentProps {
  method: EnumFormaPagamento | null;
  onValid?: (isValid: boolean) => void;
  setCurrentValue?: (value: number, index: number) => void;
  is2MethodsChildren?: boolean;
  index?: number;
}

const FormPayment = ({
  method,
  onValid,
  is2MethodsChildren,
  index,
  setCurrentValue,
}: FormPaymentProps) => {
  if (!method) {
    return null;
  }

  const key = useMemo(() => {
    return is2MethodsChildren && method === EnumFormaPagamento.CREDITO
      ? EnumFormaPagamento.MULTIPLE_CREDIT
      : method;
  }, [method, is2MethodsChildren]);

  const {
    paymentForm,
    dataPaymentBillet,
    paymentCard,
    setEnableCheckout,
    setPaymentMethods,
    installmentsToUse
  } = usePayment();

  const amount = useMemo(() => {
    const amountBillets = dataPaymentBillet?.billets?.map(
      (billet) => billet.amount.total
    );
    const amountDebits = dataPaymentBillet?.debits
      ?.filter((debit) => !debit?.redirect_to_be_paid)
      .map((debit) => debit.amount);
    const amountPix = dataPaymentBillet?.pix?.map((pix) => pix.amount.total);

    return sum([...amountBillets, ...amountDebits, ...amountPix]);
  }, [dataPaymentBillet, paymentCard]);

  const defaultValues = useMemo(() => {
    return {
      ...([EnumFormaPagamento.MULTIPLE_CREDIT, EnumFormaPagamento.CREDITO].includes(key) && {
        installments: installmentsToUse[0].installment_number,
      }),
      ...(!is2MethodsChildren &&
        [
          EnumFormaPagamento.DEBITO,
          EnumFormaPagamento.PIX,
          EnumFormaPagamento.CREDITO,
        ].includes(key as any) && {
        value: formatMoney(amount),
      }),
    };
  }, [key, amount, is2MethodsChildren]);

  const methods = useForm({
    resolver: yupResolver(handleSchemaValidation(key)),
    mode: "onChange",
    defaultValues,
  });

  const value = useWatch({
    name: "value",
    control: methods.control,
  });

  useEventBus({
    handler: () => {
      if (methods) {
        handleSubmitForm({
          ...methods?.getValues(),
          typeMethod: keyPaymentMethods[key as keyof typeof keyPaymentMethods],
        });
      }
    },
    keyEvent: "formPaymentClicked",
  });

  useEffect(() => {
    if (is2MethodsChildren) {
      methods.reset({ ...defaultValues, value: "5,00" });
    }
  }, [method]);

  const handleSubmitForm = useCallback(
    (data: any) => {
      if (key !== EnumFormaPagamento.MULTIPLE) {
        setPaymentMethods({
          data,
          isOnly: !is2MethodsChildren,
          ...(is2MethodsChildren && { index }),
        });
      }
    },
    [method, key, methods]
  );

  useEffect(() => {
    if (!onValid && methods) {
      setEnableCheckout(methods.formState.isValid);
      return;
    }

    onValid?.(methods?.formState?.isValid);
  }, [methods?.formState?.isValid]);

  useEffect(() => {
    if (setCurrentValue && index !== undefined) {
      setCurrentValue(cleanMoneyMask(value!) || 0, index);
    }
  }, [value]);

  const ComponentMethodPayment = useMemo(() => paymentMethodsDict[key], [key]);

  const isTypeForm = useMemo(() => {
    return [
      EnumFormaPagamento.CREDITO,
      EnumFormaPagamento.DEBITO,
      EnumFormaPagamento.PIX,
      EnumFormaPagamento.MULTIPLE_CREDIT,
    ].includes(key as any);
  }, [method]);

  const content = (
    <ComponentMethodPayment
      key={method}
      paymentForm={paymentForm}
      showSummary={false}
      amount={amount}
      enable={true}
      is2MethodsChildren={is2MethodsChildren ?? false}
    />
  );

  if (!isTypeForm || !methods) return content

  return <FormProvider {...methods}>{content}</FormProvider>
};

export default FormPayment;
